import * as React from "react";
import { Loader2, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { useCancelSubscription } from "@/hooks/useSubscriptions";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";

interface Props {
  subscriptionId: number;
  planName: string;
}

/**
 * Destructive action on the Billing hero. Confirms before cancelling so a
 * mis-click doesn't kill a live plan. On success the account + history
 * queries are invalidated by the hook, so the UI updates itself.
 */
export function CancelSubscriptionButton({ subscriptionId, planName }: Props) {
  const cancel = useCancelSubscription();
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);

  async function onConfirm() {
    try {
      const res = await cancel.mutateAsync(subscriptionId);
      toast({
        title: res.success ? "Subscription cancelled" : "Already cancelled",
        description: res.message
      });
      setOpen(false);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Couldn't cancel. Please try again.";
      toast({ title: "Cancellation failed", description: message, variant: "destructive" });
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" className="text-destructive hover:text-destructive sm:w-auto">
          <XCircle className="h-4 w-4" />
          Cancel plan
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel {planName}?</AlertDialogTitle>
          <AlertDialogDescription>
            Your subscription will be marked cancelled and won't renew. You'll keep access
            until the end of the current billing period. You can re-subscribe from the Plans
            page at any time.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={cancel.isPending}>Keep plan</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); void onConfirm(); }}
            disabled={cancel.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {cancel.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Cancel subscription
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
